import Vue from 'vue';
import VueRouter, { Route } from 'vue-router';
import AppRouter from '../views/AppRouter.vue';
import Login from '../views/Login.vue';
import Organization from '../views/authenticated/Organization.vue';
import Organizations from '../views/authenticated/Organizations.vue';
import OrganizationPacketGuard from '../views/authenticated/OrganizationPacketGuard.vue';
import OrganizationPacketCreatorGuard from '../views/authenticated/OrganizationPacketCreatorGuard.vue';
import OrganizationPacketCreatorLibraryGuard from '../views/authenticated/OrganizationPacketCreatorLibraryGuard.vue';
import OrganizationPeerGroupGoalsGuard from '../views/authenticated/OrganizationPeerGroupGoalsGuard.vue';
import OrganizationPeerGroupSettingsGuard from '../views/authenticated/OrganizationPeerGroupSettingsGuard.vue';
import OrganizationPeerGroupGuard from '../views/authenticated/OrganizationPeerGroupGuard.vue';
import OrganizationSettingsGuard from '../views/authenticated/OrganizationSettingsGuard.vue';
import OrganizationUserGuard from '../views/authenticated/OrganizationUserGuard.vue';
import PublicPacketGuard from '../views/authenticated/PublicPacketGuard.vue';
import SignUp from '../views/SignUp.vue';
import store from '../store';

Vue.use(VueRouter);

const routes = [
  {
    path: '/about',
    name: 'About',
    // route level code-splitting
    // this generates a separate chunk (about.[hash].js) for this route
    // which is lazy-loaded when the route is visited.
    component: () =>
      import(/* webpackChunkName: "about" */ '../views/About.vue')
  },
  {
    path: '/sign-up',
    redirect: '/sign-up/0'
  },
  {
    path: '/sign-up/:stepNo',
    name: 'SignUp',
    component: SignUp,
    props: (route: Route) => {
      return {
        stepNo: +(route.params.stepNo || 0),
        isPacketDetailsOpen: route.query.isPacketDetailsOpen === 'true',
        accountType: route.query['account-type']
      };
    }
  },
  {
    path: '/',
    redirect: '/login'
  },
  {
    // Shouldn't be hit directly? Maybe a redirect?
    path: '/app',
    redirect: '/',
    name: 'AppRouter',
    component: AppRouter,
    children: [
      {
        path: '/login',
        name: 'Login',
        component: Login,
        props: (route: Route) => {
          return {
            targetUrl: route.query.targetUrl,
            token: route.query.token
          };
        }
      },
      {
        path: '/secure/orgs',
        name: 'Organizations',
        component: Organizations
      },
      {
        path: '/secure/org/:organizationId',
        name: 'Organization',
        component: Organization,
        props: (route: Route) => {
          return {
            organizationId: +route.params.organizationId
          };
        }
      },
      {
        path: '/secure/org/:organizationId/settings',
        name: 'OrganizationSettings',
        component: OrganizationSettingsGuard,
        props: (route: Route) => {
          return {
            organizationId: +route.params.organizationId
          };
        }
      },
      {
        path: '/secure/org/:organizationId/peerGroup/:peerGroupId',
        name: 'OrganizationPeerGroup',
        component: OrganizationPeerGroupGuard,
        props: (route: Route) => {
          return {
            organizationId: +route.params.organizationId,
            peerGroupId: +route.params.peerGroupId
          };
        }
      },
      {
        path: '/secure/org/:organizationId/peerGroup/:peerGroupId/goals',
        name: 'OrganizationPeerGroupGoals',
        component: OrganizationPeerGroupGoalsGuard,
        props: (route: Route) => {
          return {
            organizationId: +route.params.organizationId,
            peerGroupId: +route.params.peerGroupId
          };
        }
      },
      {
        path: '/secure/org/:organizationId/peerGroup/:peerGroupId/settings',
        name: 'OrganizationPeerGroupSettings',
        component: OrganizationPeerGroupSettingsGuard,
        props: (route: Route) => {
          return {
            organizationId: +route.params.organizationId,
            peerGroupId: +route.params.peerGroupId
          };
        }
      },
      {
        path: '/secure/org/:organizationId/user/:userId',
        name: 'OrganizationUser',
        component: OrganizationUserGuard,
        props: (route: Route) => {
          return {
            organizationId: +route.params.organizationId,
            userId: +route.params.userId
          };
        }
      },
      {
        path: '/secure/org/:organizationId/packet/:packetId/public',
        name: 'PublicPacket',
        component: PublicPacketGuard,
        props: (route: Route) => {
          const props = {
            organizationId: +route.params.organizationId,
            packetId: +route.params.packetId
          };

          return props;
        }
      },
      {
        path: '/secure/org/:organizationId/packet/:packetId/',
        name: 'OrganizationPacket',
        component: OrganizationPacketGuard,
        props: (route: Route) => {
          const props = {
            organizationId: +route.params.organizationId,
            packetId: +route.params.packetId,
            startInEditMode: route.query.startInEditMode === 'true'
          };

          return props;
        }
      },
      {
        path: '/secure/org/:organizationId/new-packet',
        name: 'OrganizationPacketCreator',
        component: OrganizationPacketCreatorGuard,
        props: (route: Route) => {
          return {
            organizationId: +route.params.organizationId,
            peerGroupId: +route.params.peerGroupId
          };
        }
      },
      {
        path: '/secure/org/:organizationId/new-packet/find-base',
        name: 'OrganizationPacketCreatorLibrary',
        component: OrganizationPacketCreatorLibraryGuard,
        props: (route: Route) => {
          return {
            organizationId: +route.params.organizationId,
            peerGroupId: +route.params.peerGroupId
          };
        }
      }
    ]
  }
];

const router = new VueRouter({
  routes
});

router.beforeEach((to, from, next) => {
  if (to.path.startsWith('/secure') && !store.state.auth.isAuthenticated) {
    next({
      path: '/login',
      query: {
        ...to.query,
        targetUrl: to.fullPath
      }
    });
  } else {
    next();
  }
});

export default router;
